import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../Auth/hook_useAuth';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Api from '../Auth/Api';
import { decodeJWT, obterPrimeiroNome } from '../Components/jwtUtils';
import { Dropdown } from 'react-bootstrap';
import { Modal, Toast } from 'react-bootstrap';
import { useAjustes } from '../Context/AjustesContext';
import DropdownMenu from '../Components/DropdownMenu';
import { sairDoContextoEmpresa } from '../utils/superAdminContexto';

const BANNER_HEIGHT = 36;

function Header() {
    const token = JSON.parse(localStorage.getItem("user_token"))
    const claims = decodeJWT(token) || {};
    const nivel = claims.nivel;
    const ehSuperAdmin = claims.super_admin === true || nivel === 'Super Admin';
    const empresaIdAtual = claims.empresa_id ?? null;
    const empresaNomeAtual = localStorage.getItem("empresa_nome_atual") || '';
    const navigate = useNavigate();
    const bannerAtivo = ehSuperAdmin && !!empresaIdAtual;
    const superAdminSemEmpresa = ehSuperAdmin && !empresaIdAtual;
    const modoCatalogo = ehSuperAdmin && claims.catalogo === true;

    const { signout } = useAuth();
    const nome_completo = localStorage.getItem("nome_completo")
    const foto = localStorage.getItem("foto")
    const [showModalRelatorios, setShowModalRelatorios] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { ajustes } = useAjustes();

    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) {
            return path; // Already a full URL (external like Imgur)
        }
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);
    const [showToastEficacia, setShowToastEficacia] = useState(false);
    const [notificacoesEficaciaAnterior, setNotificacoesEficaciaAnterior] = useState(0);
    const [agendamentosVencidos, setAgendamentosVencidos] = useState([]);

    // Função para formatar data sem problemas de timezone
    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Se a data já vem no formato YYYY-MM-DD, usar diretamente sem conversão de timezone
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [ano, mes, dia] = dateString.split('-');
            return `${dia}/${mes}/${ano}`;
        }
        // Caso contrário, usar a data normalmente
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    // Usar informações de contato dos ajustes do banco de dados
    const emailContato = ajustes?.email_contato
    const whatsappContato = ajustes?.whatsapp_contato
    
    // Formatar telefone para exibição (adicionar parênteses e traço)
    const formatarTelefone = (numero) => {
        if (!numero) return '(00) 00000-0000';
        // Remove tudo que não é número
        const apenasNumeros = numero.replace(/\D/g, '');
        // Se for formato internacional (inicia com 55)
        if (apenasNumeros.startsWith('55') && apenasNumeros.length === 13) {
            const ddd = apenasNumeros.substring(2, 4);
            const parte1 = apenasNumeros.substring(4, 9);
            const parte2 = apenasNumeros.substring(9);
            return `(${ddd}) ${parte1}-${parte2}`;
        }
        // Se for formato nacional (11 dígitos)
        if (apenasNumeros.length === 11) {
            const ddd = apenasNumeros.substring(0, 2);
            const parte1 = apenasNumeros.substring(2, 7);
            const parte2 = apenasNumeros.substring(7);
            return `(${ddd}) ${parte1}-${parte2}`;
        }
        return numero;
    };
    
    const telefoneContato = formatarTelefone(whatsappContato);

    useEffect(() => {
        if (bannerAtivo) {
            document.body.classList.add('super-admin-visualizando');
        } else {
            document.body.classList.remove('super-admin-visualizando');
        }
        return () => document.body.classList.remove('super-admin-visualizando');
    }, [bannerAtivo]);

    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            if (offset > 10) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const [dataNotificacoes, setDataNotificacoes] = useState([]);
    
    // Buscar agendamentos vencidos
    useEffect(() => {
        // Apenas para usuários que podem gerenciar agendamentos (e Super Admin com empresa selecionada)
        if (!['Gestor', 'RH', 'Administrador'].includes(nivel)) return;
        if (ehSuperAdmin && !empresaIdAtual) return;
        if (modoCatalogo) return;

        async function fetchAgendamentosVencidos() {
            try {
                const hoje = new Date();
                hoje.setHours(0, 0, 0, 0);
                const mes = hoje.getMonth() + 1;
                const ano = hoje.getFullYear();

                const response = await Api.get(`/api/agendamentos-treinamentos?mes=${mes}&ano=${ano}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                // Filtrar agendamentos vencidos (data passou e não foi realizado/cancelado)
                // Regra: considerar vencido somente após 1 dia completo depois da data do treinamento
                const vencidos = response.data.filter(ag => {
                    if (!ag.data_treinamento) return false;
                    if (ag.status === 'realizado' || ag.status === 'cancelado') return false;
                    
                    // Tratar data no formato YYYY-MM-DD sem problemas de timezone
                    let dataTreinamento;
                    if (typeof ag.data_treinamento === 'string' && ag.data_treinamento.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        const [anoStr, mesStr, diaStr] = ag.data_treinamento.split('-');
                        const ano = parseInt(anoStr, 10);
                        const mes = parseInt(mesStr, 10) - 1;
                        const dia = parseInt(diaStr, 10);
                        dataTreinamento = new Date(ano, mes, dia);
                    } else {
                        dataTreinamento = new Date(ag.data_treinamento);
                    }
                    dataTreinamento.setHours(0, 0, 0, 0);
                    
                    const diffMs = hoje.getTime() - dataTreinamento.getTime();
                    if (diffMs <= 0) return false; // hoje antes ou no dia do treinamento
                    const diffDias = diffMs / (1000 * 60 * 60 * 24);
                    
                    // Só marcar como vencido depois de 1 dia completo após a data do treinamento
                    return diffDias >= 1;
                });

                setAgendamentosVencidos(vencidos);
            } catch (error) {
                console.error('Erro ao carregar agendamentos vencidos:', error);
            }
        }

        fetchAgendamentosVencidos();
        
        // Atualizar a cada 5 minutos
        const interval = setInterval(fetchAgendamentosVencidos, 300000);
        return () => clearInterval(interval);
    }, [token, nivel, ehSuperAdmin, empresaIdAtual, modoCatalogo]);

    useEffect(() => {
        if (ehSuperAdmin && !empresaIdAtual) return;
        if (modoCatalogo) return;
        async function fetchData() {
            try {
                const response = await Api.get(`/api/notificacoes`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                
                const novasNotificacoes = response.data;
                const notificacoesEficacia = novasNotificacoes.notificacoes?.filter(n => n.tipo === 'avaliacao_eficacia') || [];
                
                // Verificar se há novas notificações de eficácia
                if (nivel === 'Gestor' && notificacoesEficacia.length > notificacoesEficaciaAnterior && notificacoesEficaciaAnterior > 0) {
                    setShowToastEficacia(true);
                }
                
                setNotificacoesEficaciaAnterior(notificacoesEficacia.length);
                setDataNotificacoes(novasNotificacoes);
            } catch (error) {
                console.error(error.response?.data?.message || 'Erro ao carregar notificações')
            }
        }
        fetchData();
        
        // Atualizar notificações a cada 30 segundos
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [token, notificacoesEficaciaAnterior, nivel, ehSuperAdmin, empresaIdAtual, modoCatalogo])

    return (
        <>
            {bannerAtivo && (
                <div style={{
                    background: 'rgb(248 236 177)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: `${BANNER_HEIGHT}px`,
                    zIndex: 1049,
                    padding: '0 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                }}>
                    <span className="fw-semibold banner-impersonacao-texto" style={{ color: '#78350f', fontSize: '0.875rem', flex: 1, textAlign: 'center', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {modoCatalogo ? (
                            <>
                                <i className="bi bi-collection me-2" />
                                Editando o catálogo de cursos
                            </>
                        ) : (
                            <>
                                <i className="bi bi-eye-fill me-2" />
                                <span className="d-none d-sm-inline">Visualizando como: </span><strong>{empresaNomeAtual}</strong>
                            </>
                        )}
                    </span>
                    <button
                        className="btn btn-sm text-nowrap flex-shrink-0"
                        onClick={() => sairDoContextoEmpresa(modoCatalogo ? '/catalogo' : '/listar-empresas')}
                        style={{
                            background: 'rgba(0,0,0,0.15)',
                            color: '#78350f',
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            marginLeft: 'auto',
                        }}
                    >
                        {modoCatalogo ? (
                            <>
                                <i className="bi bi-arrow-left-circle me-sm-1" />
                                <span>Voltar ao catálogo</span>
                            </>
                        ) : (
                            <>
                                <i className="bi bi-x-circle me-sm-1" />
                                <span className="d-none d-sm-inline">Sair da empresa</span>
                            </>
                        )}
                    </button>
                </div>
            )}
            <header className={`header-container ${scrolled ? 'scrolled' : ''}`}>
                <nav className="navbar">
                    <div className="container-fluid content-area-padding">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div className="navbar-brand m-0">
                                {ajustes.logo_white ? (
                                    <img 
                                        className="cursor-pointer" 
                                        alt={ajustes.titulo || "Logo"} 
                                        src={getImageUrl(ajustes.logo_white)}
                                        style={{ maxHeight: '35px', maxWidth: '140px' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = './../assets/logo-white.png';
                                        }}
                                    />
                                ) : (
                                    <img 
                                        className="cursor-pointer" 
                                        alt="Logo" 
                                        src="./../assets/logo-white.png"
                                        style={{ maxHeight: '35px' }}
                                    />
                                )}
                            </div>

                            <div className="d-flex align-items-center">
                                <div className="d-flex align-items-center gap-3 gap-md-4">
                                    {!superAdminSemEmpresa && !modoCatalogo && (
                                        <Dropdown>
                                            <Dropdown.Toggle className='btn btn-none text-light p-0 fs-4 fs-md-3 px-2 py-1' id="dropdown-basic2">
                                                <i className="bi bi-whatsapp"></i>
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu className="dropdown-menu-end">
                                                <Dropdown.Item key="1" onClick={() => window.open(`mailto:${emailContato}`)}>
                                                    <div className='text-muted small'>Email</div>
                                                    <div>{emailContato}</div>
                                                </Dropdown.Item>
                                                <Dropdown.Divider />

                                                <Dropdown.Item key="2" onClick={() => window.open(`https://wa.me/${whatsappContato}`)}>
                                                    <div className='text-muted small'>WhatsApp</div>
                                                    <div>{telefoneContato}</div>
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}

                                    {!superAdminSemEmpresa && !modoCatalogo && (
                                    <Dropdown>
                                        <Dropdown.Toggle className='btn btn-none text-light p-0 fs-4 fs-md-3 px-2 py-1' id="dropdown-basic">
                                            <i className="bi bi-bell cursor-pointer"></i>
                                            {(dataNotificacoes.quantidade_notificacoes > 0 || agendamentosVencidos.length > 0) && (
                                                <span style={{ marginLeft: -15, marginTop: 5 }} className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-primary fs-8">
                                                    {dataNotificacoes.quantidade_notificacoes + agendamentosVencidos.length}
                                                </span>
                                            )}
                                            {/* Indicador especial para notificações de eficácia */}
                                            {dataNotificacoes.notificacoes && 
                                             dataNotificacoes.notificacoes.some(n => n.tipo === 'avaliacao_eficacia') && (
                                                <span style={{ marginLeft: -8, marginTop: -8 }} className="position-absolute top-0 start-100 translate-middle">
                                                    <i className="bi bi-stars text-warning fs-6"></i>
                                                </span>
                                            )}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="dropdown-menu-end" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '450px' }}>
                                            {/* Notificação de agendamentos vencidos */}
                                            {agendamentosVencidos.length > 0 && (
                                                <>
                                                    <Dropdown.Header className="bg-light text-primary fw-bold">
                                                        <i className="bi bi-bell me-2"></i>
                                                        Notificações ({dataNotificacoes.quantidade_notificacoes + agendamentosVencidos.length})
                                                    </Dropdown.Header>
                                                    <Dropdown.Item 
                                                        as={Link}
                                                        to="/agendamentos-treinamentos"
                                                        className="d-flex justify-content-between align-items-start py-2 px-3 border-0 bg-warning bg-opacity-10" 
                                                        style={{ 
                                                            whiteSpace: 'normal', 
                                                            wordWrap: 'break-word',
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#fff3cd'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 193, 7, 0.1)'}
                                                    >
                                                        <div className="d-flex align-items-start w-100">
                                                            <i className="bi bi-exclamation-triangle-fill text-warning me-2 mt-1 flex-shrink-0"></i>
                                                            <div className="flex-grow-1">
                                                                <div className="fw-bold text-warning mb-1">
                                                                    {agendamentosVencidos.length === 1 
                                                                        ? '1 Agendamento Vencido' 
                                                                        : `${agendamentosVencidos.length} Agendamentos Vencidos`}
                                                                </div>
                                                                <div className="text-muted fs-7 mb-1">
                                                                    {agendamentosVencidos.length === 1
                                                                        ? 'Há um agendamento com data vencida que ainda não foi realizado ou cancelado'
                                                                        : `Há ${agendamentosVencidos.length} agendamentos com data vencida que ainda não foram realizados ou cancelados`}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    Clique para visualizar
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Dropdown.Item>
                                                    {dataNotificacoes.notificacoes && dataNotificacoes.notificacoes.length > 0 && (
                                                        <Dropdown.Divider className="my-1" />
                                                    )}
                                                </>
                                            )}
                                            {dataNotificacoes.notificacoes && dataNotificacoes.notificacoes.length > 0 ? (
                                                <>
                                                    {agendamentosVencidos.length === 0 && (
                                                        <Dropdown.Header className="bg-light text-primary fw-bold">
                                                            <i className="bi bi-bell me-2"></i>
                                                            Notificações ({dataNotificacoes.quantidade_notificacoes})
                                                        </Dropdown.Header>
                                                    )}
                                                    {dataNotificacoes.notificacoes.map((notificacao, index) => (
                                                        <div key={`${notificacao.tipo}-${notificacao.id || notificacao.certificado_id || index}`}>
                                                            <Dropdown.Item 
                                                                className="d-flex justify-content-between align-items-start py-2 px-3 border-0" 
                                                                style={{ 
                                                                    whiteSpace: 'normal', 
                                                                    wordWrap: 'break-word',
                                                                    transition: 'background-color 0.2s ease'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                            >
                                                                {notificacao.tipo === 'avaliacao_eficacia' ? (
                                                                    <Link to={`/avaliacao-eficacia/${notificacao.certificado_id}`} className="text-decoration-none w-100">
                                                                        <div className="d-flex align-items-start">
                                                                            <i className="bi bi-stars text-warning me-2 mt-1 flex-shrink-0"></i>
                                                                            <div className="flex-grow-1">
                                                                                <div className="fw-bold text-primary mb-1">Avaliação de Eficácia</div>
                                                                                <div className="text-muted fs-7 mb-1">
                                                                                    {notificacao.nome_aluno} - {notificacao.nome_curso}
                                                                                </div>
                                                                                <div className="small text-muted">
                                                                                    Certificado emitido em {new Date(notificacao.data_emissao).toLocaleDateString('pt-BR')}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                ) : notificacao.tipo === 'agendamento_solicitacao' || notificacao.tipo_notificacao === 'agendamento_solicitacao' ? (
                                                                    <Link 
                                                                        to="/agendamentos-treinamentos" 
                                                                        className="text-decoration-none w-100"
                                                                        onClick={async () => {
                                                                            // Marcar notificação como lida
                                                                            if (notificacao.id) {
                                                                                try {
                                                                                    await Api.post(`/api/notificacoes-agendamentos/${notificacao.id}/marcar-lida`, {}, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    // Recarregar notificações
                                                                                    const response = await Api.get(`/api/notificacoes`, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    setDataNotificacoes(response.data);
                                                                                } catch (error) {
                                                                                    console.error('Erro ao marcar notificação como lida:', error);
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div className="d-flex align-items-start">
                                                                            <i className="bi bi-calendar-check text-success me-2 mt-1 flex-shrink-0"></i>
                                                                            <div className="flex-grow-1">
                                                                                <div className="fw-bold text-primary mb-1">Nova Solicitação de Agendamento</div>
                                                                                <div className="text-muted fs-7 mb-1">
                                                                                    {notificacao.nome_curso} - {notificacao.nome_solicitante}
                                                                                </div>
                                                                                <div className="small text-muted">
                                                                                    {formatDate(notificacao.data_treinamento)} às {notificacao.hora_treinamento?.substring(0, 5)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                ) : notificacao.tipo === 'agendamento_confirmacao' || notificacao.tipo_notificacao === 'agendamento_confirmacao' ? (
                                                                    <Link 
                                                                        to="/agendamentos-treinamentos" 
                                                                        className="text-decoration-none w-100"
                                                                        onClick={async () => {
                                                                            // Marcar notificação como lida
                                                                            if (notificacao.id) {
                                                                                try {
                                                                                    await Api.post(`/api/notificacoes-agendamentos/${notificacao.id}/marcar-lida`, {}, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    // Recarregar notificações
                                                                                    const response = await Api.get(`/api/notificacoes`, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    setDataNotificacoes(response.data);
                                                                                } catch (error) {
                                                                                    console.error('Erro ao marcar notificação como lida:', error);
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div className="d-flex align-items-start">
                                                                            <i className="bi bi-check-circle text-success me-2 mt-1 flex-shrink-0"></i>
                                                                            <div className="flex-grow-1">
                                                                                <div className="fw-bold text-primary mb-1">Agendamento Confirmado</div>
                                                                                <div className="text-muted fs-7 mb-1">
                                                                                    {notificacao.nome_curso}
                                                                                </div>
                                                                                <div className="small text-muted">
                                                                                    {formatDate(notificacao.data_treinamento)} às {notificacao.hora_treinamento?.substring(0, 5)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                ) : notificacao.tipo === 'reciclagem_certificado' ? (() => {
                                                                    // data_expiracao vem como YYYY-MM-DD; 'sv-SE' formata a data local no mesmo padrão
                                                                    const venceu = notificacao.data_expiracao < new Date().toLocaleDateString('sv-SE');
                                                                    const liberada = notificacao.tipo_reciclagem === 'reciclagem_liberada';
                                                                    return (
                                                                        <Link
                                                                            to={`/modulos/${notificacao.idCurso}`}
                                                                            className="text-decoration-none w-100"
                                                                            onClick={async () => {
                                                                                try {
                                                                                    await Api.post(`/api/notificacoes-certificado/${notificacao.id}/marcar-lida`, {}, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    const response = await Api.get(`/api/notificacoes`, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    setDataNotificacoes(response.data);
                                                                                } catch (error) {
                                                                                    console.error('Erro ao marcar notificação como lida:', error);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <div className="d-flex align-items-start">
                                                                                <i className={`bi ${liberada ? 'bi-arrow-repeat text-warning' : 'bi-alarm text-danger'} me-2 mt-1 flex-shrink-0`}></i>
                                                                                <div className="flex-grow-1">
                                                                                    <div className="fw-bold text-primary mb-1">
                                                                                        {liberada ? 'Reciclagem liberada' : 'Lembrete de reciclagem'}
                                                                                    </div>
                                                                                    <div className="text-muted fs-7 mb-1">
                                                                                        {notificacao.nome_curso}
                                                                                    </div>
                                                                                    <div className="small text-muted">
                                                                                        {venceu
                                                                                            ? `Seu certificado venceu em ${formatDate(notificacao.data_expiracao)}. Conclua o curso para obter um novo.`
                                                                                            : liberada
                                                                                                ? `O curso foi liberado para você refazer. Seu certificado atual continua válido até ${formatDate(notificacao.data_expiracao)}.`
                                                                                                : `Seu certificado vence em ${formatDate(notificacao.data_expiracao)} e a reciclagem ainda não foi concluída.`}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </Link>
                                                                    );
                                                                })() : notificacao.tipo === 'reinicializacao_forcada' ? (
                                                                    <div 
                                                                        className="w-100"
                                                                        onClick={async () => {
                                                                            // Marcar notificação como lida
                                                                            if (notificacao.id) {
                                                                                try {
                                                                                    await Api.post(`/api/notificacoes-reinicializacao/${notificacao.id}/marcar-lida`, {}, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    // Recarregar notificações
                                                                                    const response = await Api.get(`/api/notificacoes`, {
                                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                                    });
                                                                                    setDataNotificacoes(response.data);
                                                                                } catch (error) {
                                                                                    console.error('Erro ao marcar notificação como lida:', error);
                                                                                }
                                                                            }
                                                                        }}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <div className="d-flex align-items-start">
                                                                            <i className="bi bi-arrow-clockwise text-danger me-2 mt-1 flex-shrink-0"></i>
                                                                            <div className="flex-grow-1">
                                                                                <div className="fw-bold text-danger mb-1">Curso Reiniciado</div>
                                                                                <div className="text-muted fs-7 mb-1">
                                                                                    {notificacao.nome_curso}
                                                                                </div>
                                                                                <div className="small text-muted mb-1">
                                                                                    Seu curso foi reiniciado por um administrador. Você precisará refazer o curso do zero. Se você já tinha certificado, ele continua válido até o vencimento.
                                                                                </div>
                                                                                {notificacao.justificativa && (
                                                                                    <div className="small text-muted fst-italic">
                                                                                        <strong>Justificativa:</strong> {notificacao.justificativa}
                                                                                    </div>
                                                                                )}
                                                                                <div className="small text-muted mt-1">
                                                                                    {formatDate(notificacao.data)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Link to={`/aulas/${notificacao.id_modulo}`} className="text-decoration-none w-100">
                                                                        <div className="d-flex align-items-start">
                                                                            <i className="bi bi-file-earmark-text text-primary me-2 mt-1 flex-shrink-0"></i>
                                                                            <div className="flex-grow-1">
                                                                                <div className="fw-bold text-primary mb-1">{notificacao.nome_avaliacao}</div>
                                                                                <div className="small text-muted">por {notificacao.nome_tutor}</div>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                )}
                                                            </Dropdown.Item>
                                                            {index < dataNotificacoes.notificacoes.length - 1 && (
                                                                <Dropdown.Divider className="my-1" />
                                                            )}
                                                        </div>
                                                    ))}
                                                    <Dropdown.Divider className="my-0" />
                                                    <Dropdown.Item className="text-center py-2 bg-light">
                                                        <small className="text-muted">
                                                            <i className="bi bi-info-circle me-1"></i>
                                                            Clique nas notificações para acessar
                                                        </small>
                                                    </Dropdown.Item>
                                                </>
                                            ) : agendamentosVencidos.length === 0 ? (
                                                <Dropdown.Item className="text-center py-4">
                                                    <div className="text-muted">
                                                        <i className="bi bi-bell-slash fs-4 mb-2 d-block"></i>
                                                        Nenhuma nova notificação
                                                    </div>
                                                </Dropdown.Item>
                                            ) : null}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    )}

                                    <DropdownMenu
                                        toggle={
                                            <div className='btn btn-none d-flex align-items-center text-light gap-1 gap-md-2 px-1 px-md-2' style={{ whiteSpace: 'nowrap' }}>
                                                {foto !== 'null' ? <img alt="foto_perfil" src={`${process.env.REACT_APP_API_URL}/fotos/${foto}`} className='rounded-circle object-fit-cover border-light border border-2' width={35} height={35} /> : <i className="bi fs-2 fs-md-1 bi-person-circle cursor-pointer"></i>}
                                                <div className="d-none d-md-flex flex-column">
                                                    <span className="text-light">{obterPrimeiroNome(nome_completo)}</span>
                                                    <small className="text-light fs-7 opacity-25">{nivel}</small>
                                                </div>
                                            </div>
                                        }
                                        items={useMemo(() => {
                                            const menuItems = [
                                                { header: true, label: nome_completo, className: 'text-center text-muted' }
                                            ];

                                            if (superAdminSemEmpresa) {
                                                menuItems.push({ icon: 'buildings', label: 'Empresas', to: '/listar-empresas' });
                                                menuItems.push({ icon: 'collection', label: 'Catálogo', to: '/catalogo' });
                                                menuItems.push({ icon: 'globe2', label: 'Configurações Globais', to: '/configuracoes-globais' });
                                                menuItems.push({ divider: true });
                                                menuItems.push({ icon: 'box-arrow-right', label: 'Sair', onClick: () => [signout(), navigate("/")] });
                                                return menuItems;
                                            }

                                            if (modoCatalogo) {
                                                menuItems.push({ icon: 'book', label: 'Cursos', to: '/meus-cursos' });
                                                menuItems.push({ icon: 'journals', label: 'Tema', to: '/listar-topicos' });
                                                menuItems.push({ divider: true });
                                                menuItems.push({ icon: 'arrow-left-circle', label: 'Voltar ao catálogo', onClick: () => sairDoContextoEmpresa('/catalogo') });
                                                menuItems.push({ icon: 'box-arrow-right', label: 'Sair', onClick: () => [signout(), navigate("/")] });
                                                return menuItems;
                                            }

                                            if (nivel === 'Administrador') {
                                                menuItems.push({ icon: 'journals', label: 'Tema', to: '/listar-topicos' });
                                                menuItems.push({ icon: 'gear-wide-connected', label: 'Ajustes', to: '/ajustes' });
                                            }

                                            if (nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH') {
                                                menuItems.push({ icon: 'person', label: 'Lista de Usuários', to: '/listar-usuarios' });
                                            }

                                            if (nivel === 'Administrador' || nivel === 'RH') {
                                                menuItems.push({ icon: 'file-earmark-text', label: 'Termos', to: '/termos' });
                                            }

                                            menuItems.push({
                                                icon: 'card-checklist',
                                                label: 'Histórico Escolar',
                                                to: nivel === 'Aluno' ? '/aluno/historico-escolar' : '/historico-escolar'
                                            });

                                            if (nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH') {
                                                menuItems.push({ icon: 'clipboard2-data', label: 'Relatórios', onClick: () => setShowModalRelatorios(true) });
                                            }

                                            menuItems.push({ icon: 'gear', label: 'Configurações', to: '/configuracoes' });
                                            menuItems.push({ icon: 'box-arrow-right', label: 'Sair', onClick: () => [signout(), navigate("/")] });

                                            return menuItems;
                                        }, [nivel, nome_completo, superAdminSemEmpresa, modoCatalogo, setShowModalRelatorios, signout, navigate])}
                                        align="end"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            <Modal size="lg" show={showModalRelatorios} onHide={() => setShowModalRelatorios(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-center w-100">
                        Relatórios
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ background: "#f9f9f9" }}>
                    <div className="container mt-2">
                        <div className="text-center mb-4">
                            <h5>Selecione um Relatório</h5>
                            <p className="text-muted">Escolha o tipo de relatório que você deseja visualizar.</p>
                        </div>
                        <div className="list-group">
                            <Link
                                to="/relatorios/grupo"
                                onClick={() => setShowModalRelatorios(false)}
                                className="list-group-item list-group-item-action d-flex align-items-center p-3"
                            >
                                <i className="bi bi-people-fill display-4 text-success me-3"></i>
                                <div>
                                    <h5 className="mb-0">Aulas em Grupo</h5>
                                    <small className="text-muted">Visualize o progresso das aulas em grupo.</small>
                                </div>
                            </Link>
                            <Link
                                to="/relatorios/aluno"
                                onClick={() => setShowModalRelatorios(false)}
                                className="list-group-item list-group-item-action d-flex align-items-center p-3"
                            >
                                <i className="bi bi-person-lines-fill display-4 text-primary me-3"></i>
                                <div>
                                    <h5 className="mb-0">Progresso do Aluno</h5>
                                    <small className="text-muted">Acompanhe o progresso individual dos alunos.</small>
                                </div>
                            </Link>
                            <Link
                                to="/relatorios/acompanhamento"
                                onClick={() => setShowModalRelatorios(false)}
                                className="list-group-item list-group-item-action d-flex align-items-center p-3"
                            >
                                <i className="bi bi-credit-card-2-front display-4 text-info me-3"></i>
                                <div>
                                    <h5 className="mb-0">Acompanhamento de Treinamentos</h5>
                                    <small className="text-muted">Monitore o andamento e desempenho dos treinamentos.</small>
                                </div>
                            </Link>
                            {(nivel === 'Administrador' || nivel === 'RH') && (
                                <Link
                                    to="/relatorios/feedback"
                                    onClick={() => setShowModalRelatorios(false)}
                                    className="list-group-item list-group-item-action d-flex align-items-center p-3"
                                >
                                    <i className="bi bi-graph-up display-4 text-warning me-3"></i>
                                    <div>
                                        <h5 className="mb-0">Estatísticas de Feedback</h5>
                                        <small className="text-muted">Visualize as estatísticas das pesquisas de satisfação.</small>
                                    </div>
                                </Link>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <button className="btn btn-outline-primary" onClick={() => setShowModalRelatorios(false)}>
                        Fechar
                    </button>
                </Modal.Footer>
            </Modal>
            
            {/* Toast para novas notificações de avaliação de eficácia */}
            <Toast 
                show={showToastEficacia} 
                onClose={() => setShowToastEficacia(false)} 
                delay={4000} 
                autohide 
                className="position-fixed top-0 end-0 m-3 toast-icon toast-warning"
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">Nova Avaliação de Eficácia</strong>
                </Toast.Header>
                <Toast.Body>
                    Você tem novas avaliações de eficácia pendentes para responder. 
                    Clique no sino para visualizar.
                </Toast.Body>
            </Toast>
        </>
    );
}

export default Header;
